import { Invoice } from "../services/API/Invoices";

export interface DuplicateCandidate {
  supplierOrgId: string;
  contractId: string;
  invoiceNumber: string;
  billingDate: string;
  amount: number | string;
}

export const normalizeDuplicateBillingDate = (value: unknown): string => {
  if (!value) return "";
  return value.toString().split("T")[0].trim();
};

export const normalizeDuplicateAmount = (value: unknown): number => {
  const candidate =
    typeof value === "object" && value !== null
      ? ((value as any).parsedValue ?? (value as any).source ?? 0)
      : value;

  if (typeof candidate === "number") {
    return Number(candidate.toFixed(2));
  }

  const parsed = parseFloat(
    String(candidate ?? "")
      .replace(/\s/g, "")
      .replace(",", "."),
  );

  if (Number.isNaN(parsed)) return 0;
  return Number(parsed.toFixed(2));
};

export const buildDuplicateBaseKey = (
  supplierOrgId: string,
  contractId: string,
  invoiceNumber: string,
): string => {
  return [
    String(supplierOrgId ?? "").trim(),
    String(invoiceNumber ?? "").trim(),
    String(contractId ?? "").trim(),
  ].join("#");
};

export const getExistingInvoiceSupplierOrgId = (item: any): string => {
  return String(item.supplierOrgId ?? item.SupplierOrgId ?? "").trim();
};

export const getExistingInvoiceContractId = (item: any): string => {
  return String(item.contractId ?? item.ContractId ?? "").trim();
};

export const getExistingInvoiceNumber = (item: any): string => {
  return String(
    item.supplierInvoiceNumber ??
      item.SupplierInvoiceNumber ??
      item.invoiceNumber ??
      item.InvoiceNumber ??
      "",
  ).trim();
};

export const getExistingInvoiceBillingDate = (item: any): string => {
  return String(item.billingDate ?? item.BillingDate ?? "");
};

export const getExistingInvoiceAmount = (item: any): number => {
  return normalizeDuplicateAmount(
    item.priceExcMoms ??
      item.PriceExcMoms ??
      item.sumExclMoms ??
      item.SumExclMoms ??
      0,
  );
};

export const groupExistingInvoicesByDuplicateKey = (
  data: Invoice[],
): Record<string, Invoice[]> => {
  return data.reduce((acc: Record<string, Invoice[]>, item) => {
    const key = buildDuplicateBaseKey(
      getExistingInvoiceSupplierOrgId(item),
      getExistingInvoiceContractId(item),
      getExistingInvoiceNumber(item),
    );

    if (!acc[key]) {
      acc[key] = [];
    }

    acc[key].push(item);
    return acc;
  }, {});
};

export const isDuplicateByClientRule = (
  candidate: DuplicateCandidate,
  keyedInvoices: Record<string, Invoice[]>,
): boolean => {
  const baseKey = buildDuplicateBaseKey(
    candidate.supplierOrgId,
    candidate.contractId,
    candidate.invoiceNumber,
  );

  const existingInvoices = keyedInvoices[baseKey] ?? [];
  if (existingInvoices.length === 0) return false;

  const normalizedBillingDate = normalizeDuplicateBillingDate(
    candidate.billingDate,
  );
  const normalizedAmount = normalizeDuplicateAmount(candidate.amount);

  return existingInvoices.some((existingInvoice) => {
    const existingBillingDate = normalizeDuplicateBillingDate(
      getExistingInvoiceBillingDate(existingInvoice),
    );
    const existingAmount = getExistingInvoiceAmount(existingInvoice);

    const sameDate = existingBillingDate === normalizedBillingDate;
    const sameAmount = existingAmount === normalizedAmount;

    return !(sameDate && !sameAmount);
  });
};
